import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Grid, Card, CardContent, IconButton, Button, Tabs, Tab, Alert, Paper, Backdrop, CircularProgress, Pagination, Checkbox, TextField, Dialog, DialogContent, FormControl, InputLabel, Select, MenuItem, Stack, Divider, Container, Fade, Slide, Zoom,  } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useMutation, useQuery } from "react-query";
import { Delete, FileCopy, Share, Add, PlayArrow, CheckCircle, Schedule } from "@mui/icons-material";
import client from "../../../../API";
import CastingCallDialog from "./castingcallsdialog";
import CreateCallDialog from "./CreateCallDialog";
import DealIOCreateForm from "../../../DealsIO/dealscreate";
import { globalStyles } from "../../../../Utils/Styles";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useAuth from "../../../../Hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { 
  CardMedia, 
  Chip, 
  CardActions 
} from "@mui/material";

import { Add as AddIcon } from "@mui/icons-material";

const dummyPhotoLinks = [
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/287262.jpg",
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/wp2468668.jpg",
  "https://storage.googleapis.com/blitzccmc.appspot.com/1/eev9l01j44ab1.jpg",
];

const castingRowsPerPage = 12;
const statusColorMap = {
  ACTIVE: 'success',
  COMPLETED: 'info',
  DELETED: 'error'
};

const statusIcons = {
  ACTIVE: <PlayArrow />,
  COMPLETED: <CheckCircle />,
  DELETED: <Delete />
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? "Invalid Date" : 
    `${(date.getMonth()+1).toString().padStart(2,'0')}/${date.getDate().toString().padStart(2,'0')}/${date.getFullYear().toString().substr(-2)}`;
};

const summarizeText = (text, maxLength = 50) => {
  return text.length <= maxLength ? text : text.substring(0, maxLength) + "...";
};

const CastingCalls = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('casting');
  const [bannerVisible, setBannerVisible] = useState(false);

  // Casting Calls State
  const [castingTabValue, setCastingTabValue] = useState("all");
  const [selectedCastingCallId, setSelectedCastingCallId] = useState(null);
  const [openCastingCreate, setOpenCastingCreate] = useState(false);
  const [castingPage, setCastingPage] = useState(0);
  const [castingRowCount, setCastingRowCount] = useState(0);

  // DealIOs State
  const [dealIOs, setDealIOs] = useState([]);
  const [filteredDealIOs, setFilteredDealIOs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDealIOs, setSelectedDealIOs] = useState([]);
  const [selectedAction, setSelectedAction] = useState("");
  const [openDealCreate, setOpenDealCreate] = useState(false);

  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();

  // Casting Calls Data
  const { 
    data: castingCalls, 
    isFetching: loadingCasting, 
    refetch: fetchCastingCalls, 
    error: castingError 
  } = useQuery({
    queryKey: ['castingCalls', 'list', { castingPage, castingTabValue }],
    queryFn: () => client.castingCalls.list({ page: castingPage, rows: castingRowsPerPage, status: castingTabValue }),
    enabled: currentView === 'casting',
    refetchOnWindowFocus: false
  });

  // DealIOs Data
  const { 
    data: dealIOsData, 
    isFetching: loadingDeals, 
    refetch: fetchDealIOs 
  } = useQuery({
    queryKey: ['dealIOs', 'list'],
    queryFn: () => client.dealsIO.list(),
    enabled: currentView === 'deals',
    onSuccess: (data) => {
      setDealIOs(data);
      setFilteredDealIOs(data);
    }
  });

  useEffect(() => {
    if (castingCalls) {
      setCastingRowCount(castingCalls.total);
    }
  }, [castingCalls]);

  // Casting Calls Mutations
  const { mutate: updateCastingStatus } = useMutation(
    ({ id, status }) => client.castingCalls.updateStatus(id, status),
    { onSuccess: fetchCastingCalls }
  );

  // DealIOs Mutations
  const { mutateAsync: deleteDealIO } = useMutation(client.dealsIO.delete, {
    onSuccess: () => fetchDealIOs()
  });

  const { mutateAsync: copyDealIO } = useMutation(
    ({ dealIOId }) => client.dealsIO.copy({ dealIOId }),
    { onSuccess: () => fetchDealIOs() }
  );

  const handleViewChange = (event, newValue) => setCurrentView(newValue);

  // Casting Call Handlers
  const handleCreateCastingOpen = () => {
    const hasOpenCall = castingCalls?.casting_calls.some(
      call => call.status === "ACTIVE" && call.user_id === userInfo.id
    );
    if (hasOpenCall) {
      setBannerVisible(true);
      return;
    }
    setOpenCastingCreate(true);
  };

  const handleCastingDelete = (id) => updateCastingStatus({ id, status: "DELETED" });
  const handleCastingComplete = (id) => updateCastingStatus({ id, status: "COMPLETED" });

  // DealIO Handlers
  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredDealIOs(dealIOs.filter(deal => 
      deal.user_data.some(user => 
        user.role === "Owner" && user.name.toLowerCase().includes(query)
      )
    ));
  };

  const handleCheckboxChange = (dealIOId) => {
    const selectedIndex = selectedDealIOs.indexOf(dealIOId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedDealIOs, dealIOId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedDealIOs.slice(1));
    } else if (selectedIndex === selectedDealIOs.length - 1) {
      newSelected = newSelected.concat(selectedDealIOs.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedDealIOs.slice(0, selectedIndex),
        selectedDealIOs.slice(selectedIndex + 1)
      );
    }
    setSelectedDealIOs(newSelected);
  };

  const handleDealAction = async () => {
    if (selectedAction === "delete" && selectedDealIOs.length > 0) {
      await Promise.all(selectedDealIOs.map(id => deleteDealIO(id)));
    }
    setSelectedDealIOs([]);
  };

  const handleCopyDealIO = async (dealId) => {
    try {
      await copyDealIO({ dealIOId: dealId });
    } catch (error) {
      console.error("Failed to copy Deal IO:", error);
    }
  };

  const handleShareDealIO = (dealId) => {
    navigator.clipboard.writeText(`${window.location.origin}/deals/public/${dealId}`);
  };

  return (
    <Box sx={{ 
      ...globalStyles.wholeHeightMinusToolbar, 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
      minHeight: '100vh'
    }}>
      <Backdrop open={loadingCasting || loadingDeals} sx={{ zIndex: theme.zIndex.drawer - 1 }}>
        <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
      </Backdrop>

      {/* Modern Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          pt: 4,
          pb: 3,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.light, 0.3)} 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 50%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 1,
                background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              {currentView === 'casting' ? 'Casting Calls' : 'Deal Factoring'}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
              {currentView === 'casting' 
                ? 'Connect with creators and build amazing partnerships' 
                : 'Manage your deal factoring operations efficiently'
              }
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={currentView === 'casting' ? handleCreateCastingOpen : () => setOpenDealCreate(true)}
              startIcon={<Add />}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                py: 1.5,
                px: 4,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.25)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                }
              }}
            >
              New {currentView === 'casting' ? 'Casting Call' : 'Deal IO'}
            </Button>

            <Tabs 
              value={currentView} 
              onChange={handleViewChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  minWidth: 120,
                  '&.Mui-selected': {
                    color: 'white'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                  borderRadius: 3
                }
              }}
            >
              <Tab label="Casting Calls" value="casting" />
              <Tab label="Deal Factoring" value="deals" />
            </Tabs>
          </Box>

          {currentView === 'casting' && (
            <Box sx={{ mt: 3 }}>
              <Tabs 
                value={castingTabValue} 
                onChange={(e, v) => setCastingTabValue(v)}
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.6)',
                    fontWeight: 500,
                    minWidth: 80,
                    '&.Mui-selected': {
                      color: 'white'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    height: 2
                  }
                }}
              >
                <Tab label="All" value="all" />
                <Tab label="Active" value="active" />
                <Tab label="Completed" value="completed" />
              </Tabs>
            </Box>
          )}
        </Container>
      </Paper>

      {currentView === 'casting' ? (
        <Container maxWidth="xl" sx={{ flex: 1, py: 4 }}>
          {bannerVisible && (
            <Slide direction="down" in={bannerVisible} mountOnEnter unmountOnExit>
              <Alert 
                severity="warning" 
                onClose={() => setBannerVisible(false)}
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                You may only have one active casting call at a time.
              </Alert>
            </Slide>
          )}

          <Grid container spacing={3}>
            {castingCalls?.casting_calls.map((call, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={call.id}>
                <Zoom in timeout={300 + index * 100}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[20],
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => setSelectedCastingCallId(call.id)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={dummyPhotoLinks[Math.round((call.date_created.getTime() / 1000) % dummyPhotoLinks.length)]}
                        alt="Casting Call"
                        sx={{ objectFit: 'cover' }}
                      />
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          right: 12,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Chip 
                          icon={statusIcons[call.status]}
                          label={call.status} 
                          color={statusColorMap[call.status]} 
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            backdropFilter: 'blur(10px)',
                            bgcolor: theme.palette[statusColorMap[call.status]].main,
                            color: 'white'
                          }}
                        />
                        <Chip 
                          label={`#${call.id}`}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 700,
                          color: 'primary.main',
                          mb: 2
                        }}
                      >
                        {call.platforms.join(", ")}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        paragraph 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {call.brief}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          mt: 'auto',
                          pt: 2,
                          borderTop: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            Budget
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight="700"
                            sx={{ 
                              color: 'success.main',
                              fontSize: '0.9rem'
                            }}
                          >
                            ${call.budget_min || 0} - ${call.budget_max || 0}
                          </Typography>
                        </Stack>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCastingDelete(call.id);
                        }} 
                        sx={{ 
                          color: 'error.main',
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.error.main, 0.1) 
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                      
                      {castingTabValue !== 'completed' && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCastingComplete(call.id);
                          }}
                          sx={{ 
                            color: call.status === 'COMPLETED' ? 'success.main' : 'text.secondary',
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.success.main, 0.1) 
                            }
                          }}
                        >
                          {call.status === 'COMPLETED' ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                        </IconButton>
                      )}
                    </CardActions>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={Math.ceil(castingRowCount / castingRowsPerPage)}
              page={castingPage + 1}
              onChange={(e, v) => setCastingPage(v - 1)}
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 600,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }
                }
              }}
            />
          </Box>
        </Container>
      ) : (
        <Container maxWidth="xl" sx={{ flex: 1, py: 4 }}>
          {/* Deal Factoring Controls */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                variant="outlined"
                size="medium"
                placeholder="Search deals by owner..."
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ 
                  flexGrow: 1,
                  minWidth: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }
                }}
              />
              
              <FormControl variant="outlined" size="medium" sx={{ minWidth: 180 }}>
                <InputLabel>Bulk Action</InputLabel>
                <Select 
                  value={selectedAction} 
                  onChange={(e) => setSelectedAction(e.target.value)} 
                  label="Bulk Action"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="edit">Edit Selected</MenuItem>
                  <MenuItem value="delete">Delete Selected</MenuItem>
                </Select>
              </FormControl>
              
              <Button 
                variant="contained" 
                onClick={handleDealAction}
                disabled={!selectedAction || selectedDealIOs.length === 0}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600
                }}
              >
                Apply ({selectedDealIOs.length})
              </Button>
            </Box>
          </Paper>

          {/* Deal IO Grid */}
          <Grid container spacing={3}>
            {filteredDealIOs.map((deal, index) => (
              <Grid item xs={12} sm={6} md={4} key={deal.id}>
                <Fade in timeout={300 + index * 50}>
                  <Card 
                    sx={{ 
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: selectedDealIOs.includes(deal.id) ? 'primary.main' : 'divider',
                      bgcolor: selectedDealIOs.includes(deal.id) ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Checkbox
                          checked={selectedDealIOs.includes(deal.id)}
                          onChange={() => handleCheckboxChange(deal.id)}
                          sx={{ 
                            mt: -1,
                            '& .MuiSvgIcon-root': { 
                              fontSize: 24 
                            }
                          }}
                        />
                        
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              mb: 1,
                              color: 'primary.main',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {summarizeText(deal.name, 30)}
                          </Typography>
                          
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Created: {formatDate(deal.created_at)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                    
                    <Divider />
                    
                    <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                      <IconButton 
                        onClick={() => handleCopyDealIO(deal.id)}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.1) 
                          }
                        }}
                      >
                        <FileCopy />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleShareDealIO(deal.id)}
                        sx={{ 
                          color: 'success.main',
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.success.main, 0.1) 
                          }
                        }}
                      >
                        <Share />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Dialogs */}
      <CreateCallDialog open={openCastingCreate} onClose={() => setOpenCastingCreate(false)} />
      <CastingCallDialog open={!!selectedCastingCallId} onClose={() => setSelectedCastingCallId(null)} />
      
      <Dialog 
        open={openDealCreate} 
        onClose={() => setOpenDealCreate(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <DealIOCreateForm 
            onClose={() => setOpenDealCreate(false)} 
            onCreateSuccess={fetchDealIOs} 
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CastingCalls;